import OrderSeatBadges from '../../components/OrderSeatBadges';
/**
 * 司机端订单详情页面
 * 手机端优先设计
 * 
 * 使用集中式API配置和DriverAPI服务
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL, DriverAPI, TokenManager, isProductionEnvironment, isPreviewEnvironment, openDriverPdfDownloadUrl, resolveDriverDownloadUrl } from './config';
import { getOrderNo } from '../../utils/orderDisplay';
import DriverBrandHeader from './DriverBrandHeader';
import DriverTripInstruction from './DriverTripInstruction';
import { captureDriverStatusLocation } from '../../services/driverApp/locationService';
import { playDriverSuccessFeedback } from '../../services/driverApp/feedbackService';
import { openMapsForOrder } from '../../services/driverApp/mapService';
import { callPhoneNumber } from '../../services/driverApp/phoneService';
import {
  DRIVER_CALENDAR_ADDED_EVENT,
  addDriverOrderToCalendar,
  getDriverCalendarAdded,
} from '../../services/driverApp/calendarService';
import {
  createDriverClientEventId,
  DRIVER_STATUS_QUEUE_CHANGED_EVENT,
  flushDriverStatusQueue,
  getQueuedDriverStatusUpdates,
  loadCachedDriverOrderDetail,
  queueDriverStatusUpdate,
  saveCachedDriverOrderDetail,
} from '../../services/driverApp/offlineQueueService';
import {
  loadCachedDriverOfflineOrderPack,
  loadCachedDriverOfflineSignboardPdf,
  openCachedDriverOfflineSignboardPdf,
} from '../../services/driverApp/offlineOrderPackService';
import { formatDriverDate, getDriverLanguage, getDriverText } from './driverI18n';
import { getDriverFeatureText, getLocalizedDriverApiError } from './driverFeatureI18n';

const formatYen = (amount) => `¥${Math.round(amount || 0).toLocaleString()}`;

const getCalendarErrorMessage = (err, text) => {
  const message = err?.message || "";
  if (message === "ORDER_DATE_REQUIRED") return text.calendarDateMissing;
  if (message.includes("CALENDAR_PERMISSION_DENIED")) return text.calendarPermissionDenied;
  if (message.includes("not implemented") || message.includes("CALENDAR_PRESENTER_UNAVAILABLE")) {
    return text.calendarNativeUnavailable;
  }
  return text.submitFailed;
};

// 司机端车型显示统一使用系统标准英文名称，避免同一车型一会儿 alpha、一会儿阿尔法。
const driverVehicleTypeNames = {
  alpha: 'alphard',
  alphard: 'alphard',
  '阿尔法': 'alphard',
  hiace: 'hiace',
  premium_hiace: 'premium_hiace',
  coaster: 'coaster',
  bus: 'bus',
  hiace_14_seats: 'hiace_14_seats',
};

const formatDriverVehicleType = (vehicleType) => {
  const raw = String(vehicleType || '').trim();
  if (!raw) return '-';
  return driverVehicleTypeNames[raw] || driverVehicleTypeNames[raw.toLowerCase()] || raw;
};

const getOrderChangeHighlights = (order, text) => {
  const labelMap = {
    time: text.orderChangeTime,
    pickup: text.orderChangePickup,
    dropoff: text.orderChangeDropoff,
    note: text.orderChangeNote,
    cancelled: text.orderChangeCancelled,
  };
  return (order?.driver_change_highlights || [])
    .map((highlight) => labelMap[highlight.field])
    .filter(Boolean);
};

const DRIVER_STATUS_ACTIONS = [
  { status: 'arrived_pickup' },
  { status: 'passenger_onboard' },
  { status: 'completed' },
];

const DRIVER_PRE_PICKUP_STATUSES = new Set(['assigned', 'confirmed', 'driver_departed']);

const DRIVER_ISSUE_OPTIONS = [
  { value: 'unable_contact_passenger' },
  { value: 'passenger_no_show' },
  { value: 'passenger_late' },
  { value: 'flight_or_train_delay' },
  { value: 'pickup_location_problem' },
  { value: 'route_or_destination_changed' },
  { value: 'extra_waiting_time' },
  { value: 'luggage_or_item_left' },
  { value: 'vehicle_or_traffic_issue' },
  { value: 'order_information_problem' },
  { value: 'other' },
];

const DRIVER_EMERGENCY_DISPATCH_PHONE = '07036670926';

function DriverStatusSwipeControl({ action, disabled, updating, onComplete, text }) {
  const trackRef = useRef(null);
  const swipeRef = useRef(null);
  const progressRef = useRef(0);
  const draggingRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const dragMetricsRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const applyProgress = useCallback((nextProgress, { immediate = false } = {}) => {
    const clampedProgress = Math.max(0, Math.min(1, nextProgress));
    progressRef.current = clampedProgress;

    const updateStyle = () => {
      const track = trackRef.current;
      const swipe = swipeRef.current;
      if (!track || !swipe) return;

      const metrics = dragMetricsRef.current || {
        width: track.getBoundingClientRect().width,
      };
      const thumbTravel = Math.max(0, metrics.width - 62);
      swipe.style.setProperty('--swipe-progress', String(clampedProgress));
      swipe.style.setProperty('--swipe-thumb-x', `${Math.round(thumbTravel * clampedProgress * 10) / 10}px`);
      swipe.style.setProperty('--swipe-fill-width', `${Math.round((62 + thumbTravel * clampedProgress) * 10) / 10}px`);
    };

    if (immediate) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      updateStyle();
      return;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;
      updateStyle();
    });
  }, []);

  const calculateProgress = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = dragMetricsRef.current || track.getBoundingClientRect();
    const thumbTravelInset = 62;
    const thumbCenterOffset = 31;
    const usableWidth = Math.max(1, rect.width - thumbTravelInset);
    const raw = (clientX - rect.left - thumbCenterOffset) / usableWidth;
    return Math.max(0, Math.min(1, raw));
  }, []);

  const startDrag = (event) => {
    if (disabled || updating) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    dragMetricsRef.current = {
      left: rect.left,
      width: rect.width,
    };
    activePointerIdRef.current = event.pointerId;
    draggingRef.current = true;
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    applyProgress(calculateProgress(event.clientX), { immediate: true });
  };

  const moveDrag = (event) => {
    if (!draggingRef.current || event.pointerId !== activePointerIdRef.current) return;
    event.preventDefault();
    applyProgress(calculateProgress(event.clientX));
  };

  const finishDrag = async (event) => {
    if (!draggingRef.current || event.pointerId !== activePointerIdRef.current) return;
    const finalProgress = calculateProgress(event.clientX);
    draggingRef.current = false;
    activePointerIdRef.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (finalProgress >= 0.74) {
      applyProgress(1, { immediate: true });
      await onComplete(action.status);
      applyProgress(0);
      dragMetricsRef.current = null;
      return;
    }
    applyProgress(0);
    dragMetricsRef.current = null;
  };

  const cancelDrag = () => {
    draggingRef.current = false;
    activePointerIdRef.current = null;
    dragMetricsRef.current = null;
    setDragging(false);
    applyProgress(0);
  };

  useEffect(() => () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  return (
    <div
      ref={swipeRef}
      className={`driver-status-swipe ${dragging ? 'is-dragging' : ''} ${updating ? 'is-updating' : ''}`}
      style={{ '--swipe-progress': 0, '--swipe-thumb-x': '0px', '--swipe-fill-width': '62px' }}
      data-testid={`driver-status-${action.status}`}
    >
      <div
        ref={trackRef}
        className="driver-status-swipe-track"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || updating}
        aria-label={action.label}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={finishDrag}
        onPointerCancel={cancelDrag}
      >
        <div className="driver-status-swipe-fill" />
        <div className="driver-status-swipe-copy">
          <span>{action.label}</span>
          <small>{text.swipeToConfirm}</small>
        </div>
        <div className="driver-status-swipe-thumb" aria-hidden="true">
          <span>›</span>
        </div>
      </div>
    </div>
  );
}

// 地区名称映射
const TIME_OPTIONS = [
  '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
  '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30',
];

export default function DriverOrderDetail({ driver, onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingCachedData, setUsingCachedData] = useState(false);
  
  // 汇报包车时间相关状态
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportEndTime, setReportEndTime] = useState('');
  const [reporting, setReporting] = useState(false);
  const [generatingSignboard, setGeneratingSignboard] = useState(false);
  const [, setCalendarStateVersion] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState('');
  const [, setStatusUpdateMessage] = useState('');
  const [statusSuccess, setStatusSuccess] = useState('');
  const [queuedStatusCount, setQueuedStatusCount] = useState(0);
  const [statusSyncMessage, setStatusSyncMessage] = useState('');
  const [showIssueReportPanel, setShowIssueReportPanel] = useState(false);
  const [showMapConfirm, setShowMapConfirm] = useState(false);
  const [mapTarget, setMapTarget] = useState('route');
  const [mapProvider, setMapProvider] = useState('apple');
  const [selectedIssueTypes, setSelectedIssueTypes] = useState([]);
  const [issueNote, setIssueNote] = useState('');
  const [showReturnPanel, setShowReturnPanel] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const canViewRevenueStats = driver?.can_view_revenue_stats === true;
  
  // Safari 兼容 - 使用 ref 操作原生 DOM
  const selectRef = useRef(null);
  const driverLanguage = getDriverLanguage();
  const text = getDriverText(driverLanguage);
  const featureText = getDriverFeatureText(driverLanguage);
  const offlineOrderPackDriverId = String(driver?.id || TokenManager.getDriverInfo()?.id || '').trim();
  const statusLabel = (status) => text.status?.[String(status || '').toLowerCase()] || text.unknownStatus;
  const driverStatusLabel = (status) => text.driverStatus?.[String(status || '').toLowerCase()] || text.unknownStatus;
  const serviceTypeLabel = (serviceType) => text.serviceType?.[String(serviceType || '').toLowerCase()] || text.unknownStatus;
  const actionLabel = (status) => text.driverActions?.[String(status || '').toLowerCase()] || text.unknownStatus;
  const issueLabel = (value) => text.issueOptions?.[value] || text.unknownStatus;
  const visibleExtraServices = (order?.extra_services || []).filter(service =>
    !['child_seat', 'infant_seat'].includes(service?.service_type || service) &&
    !['cancelled', 'canceled', 'rejected', 'deleted'].includes(service?.status)
  );
  const extraServiceLabel = (service) => (
    ['child_seat', 'infant_seat'].includes(service.service_type)
      ? `${text.extraServiceTypes?.[service.service_type] || service.service_type} x${service.quantity || 1}`
      : service.service_type === 'extended_waiting_time'
        ? (() => {
            const quantity = Math.max(1, Number(service.quantity) || 1);
            const pickupArea = String(order?.pickup_area || '').toLowerCase();
            const baseMinutes = pickupArea.includes('airport') || pickupArea.includes('port') ? 90 : 30;
            return `${text.extraServiceTypes?.extended_waiting_time || service.service_type} +${quantity * 30} min / ${baseMinutes + quantity * 30} min`;
          })()
      : text.extraServiceTypes?.[service.service_type] || service.description || service.service_type
  );
  const statusPendingMessage = useCallback((count) => `${text.statusPendingCount}: ${count}`, [text]);

  const fetchOrderDetail = useCallback(async () => {
    console.log('[DriverOrderDetail] ============================================');
    console.log('[DriverOrderDetail] API URL:', API_BASE_URL);
    console.log(`[DriverOrderDetail] Environment: ${isProductionEnvironment() ? 'PRODUCTION' : isPreviewEnvironment() ? 'PREVIEW' : 'LOCAL'}`);
    console.log('[DriverOrderDetail] ============================================');
    
    try {
      // 检查token
      if (!TokenManager.isAuthenticated()) {
        console.log('[DriverOrderDetail] No token found, redirecting to login');
        setError(text.loginExpired);
        setTimeout(() => onLogout(), 1500);
        return;
      }
      
      console.log('[DriverOrderDetail] Fetching order via DriverAPI...');
      
      // 使用集中式DriverAPI
      const data = await DriverAPI.getOrderDetail(orderId);
      
      setOrder(data);
      saveCachedDriverOrderDetail(orderId, data);
      setUsingCachedData(false);
      setError('');
      
      // 如果已有汇报时间，设置为默认值
      if (data.reported_end_time) {
        setReportEndTime(data.reported_end_time);
      }
    } catch (err) {
      // 检查是否是安全检查失败
      if (err.message && err.message.includes('Safety Check Failed')) {
        console.error('[DriverOrderDetail] ❌ API URL SAFETY CHECK FAILED');
        setError(text.systemConfigError);
        setLoading(false);
        return;
      }
      
      console.log('[DriverOrderDetail] fetchOrderDetail error:', err.response?.status, err.message);
      
      // 401会被拦截器自动处理
      if (err.response?.status === 401) {
        setError(text.loginExpired);
      } else if (err.response?.status === 403) {
        setError(text.accountDisabled);
      } else if (err.response?.status === 404) {
        setError(text.orderFetchFailed);
      } else if (!err.response) {
        const offlinePack = await loadCachedDriverOfflineOrderPack(offlineOrderPackDriverId, orderId);
        const cached = offlinePack?.order ? offlinePack : loadCachedDriverOrderDetail(orderId);
        if (cached?.order) {
          setOrder(cached.order);
          setUsingCachedData(true);
          setError('');
        } else {
          setError(text.networkError);
        }
      } else {
        setError(`${text.orderFetchFailed} (${err.response?.status || text.unknownError})`);
      }
    } finally {
      setLoading(false);
    }
  }, [offlineOrderPackDriverId, orderId, onLogout, text]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  useEffect(() => {
    const refreshCalendarState = () => setCalendarStateVersion((version) => version + 1);
    window.addEventListener(DRIVER_CALENDAR_ADDED_EVENT, refreshCalendarState);
    return () => window.removeEventListener(DRIVER_CALENDAR_ADDED_EVENT, refreshCalendarState);
  }, []);

  const syncQueuedDriverStatusUpdates = useCallback(async () => {
    const queue = getQueuedDriverStatusUpdates(offlineOrderPackDriverId);
    setQueuedStatusCount(queue.length);
    if (!queue.length) {
      setStatusSyncMessage('');
      return;
    }

    setStatusSyncMessage(text.statusSyncing);
    try {
      const result = await flushDriverStatusQueue((queuedOrderId, status, payload) => (
        DriverAPI.updateOrderStatus(queuedOrderId, status, payload)
      ), offlineOrderPackDriverId);
      setQueuedStatusCount(result.remaining);
      if (result.flushed > 0) {
        setStatusSuccess(text.statusSynced);
        fetchOrderDetail();
      }
      setStatusSyncMessage(result.remaining > 0 ? statusPendingMessage(result.remaining) : '');
    } catch {
      const remaining = getQueuedDriverStatusUpdates(offlineOrderPackDriverId).length;
      setQueuedStatusCount(remaining);
      setStatusSyncMessage(remaining ? statusPendingMessage(remaining) : '');
    }
  }, [fetchOrderDetail, offlineOrderPackDriverId, statusPendingMessage, text]);

  useEffect(() => {
    const updateQueuedCount = () => {
      const count = getQueuedDriverStatusUpdates(offlineOrderPackDriverId).length;
      setQueuedStatusCount(count);
      setStatusSyncMessage(count ? statusPendingMessage(count) : '');
    };

    updateQueuedCount();
    syncQueuedDriverStatusUpdates();
    window.addEventListener('online', syncQueuedDriverStatusUpdates);
    window.addEventListener(DRIVER_STATUS_QUEUE_CHANGED_EVENT, updateQueuedCount);
    const queueSyncInterval = window.setInterval(syncQueuedDriverStatusUpdates, 15000);

    return () => {
      window.removeEventListener('online', syncQueuedDriverStatusUpdates);
      window.removeEventListener(DRIVER_STATUS_QUEUE_CHANGED_EVENT, updateQueuedCount);
      window.clearInterval(queueSyncInterval);
    };
  }, [offlineOrderPackDriverId, statusPendingMessage, syncQueuedDriverStatusUpdates, text]);

  // Safari 兼容 - 使用原生 DOM API 填充 select 选项
  useEffect(() => {
    if (showReportModal && selectRef.current) {
      const select = selectRef.current;
      // 清空现有选项
      select.innerHTML = '';
      
      // 添加默认选项
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.text = text.selectEndTime;
      select.appendChild(defaultOption);
      
      // 添加时间选项
      TIME_OPTIONS.forEach(time => {
        const option = document.createElement('option');
        option.value = time;
        option.text = time;
        select.appendChild(option);
      });
      
      // 设置当前值
      if (reportEndTime) {
        select.value = reportEndTime;
      }
    }
  }, [showReportModal, reportEndTime, text]);

  // 提交汇报时间
  const handleReportEndTime = async () => {
    if (!reportEndTime) {
      alert(text.selectEndTime);
      return;
    }
    
    setReporting(true);
    try {
      console.log('[DriverOrderDetail] Reporting end time via DriverAPI...');
      
      // 使用集中式DriverAPI
      await DriverAPI.reportEndTime(orderId, reportEndTime);
      
      alert(text.reportSuccess);
      setShowReportModal(false);
      fetchOrderDetail(); // 刷新订单数据
    } catch (err) {
      if (err.message && err.message.includes('Safety Check Failed')) {
        alert(text.systemConfigError);
      } else {
        alert(getLocalizedDriverApiError(err, text.reportFailed, driverLanguage));
      }
    } finally {
      setReporting(false);
    }
  };

  const handleDownloadSignboardPdf = async () => {
    setGeneratingSignboard(true);
    try {
      const result = await DriverAPI.createSignboardPdfDownloadLink(orderId);
      await openDriverPdfDownloadUrl(result.download_url);
    } catch (err) {
      if (err.message && err.message.includes('Safety Check Failed')) {
        alert(text.systemConfigError);
      } else if (!err.response) {
        const cachedPdf = await loadCachedDriverOfflineSignboardPdf(offlineOrderPackDriverId, orderId);
        if (cachedPdf) {
          openCachedDriverOfflineSignboardPdf(cachedPdf);
        } else {
          alert(text.offlineSignboardUnavailable);
        }
      } else {
        alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
      }
    } finally {
      setGeneratingSignboard(false);
    }
  };

  const openCalendarDownloadUrl = (downloadUrl) => {
    window.location.assign(resolveDriverDownloadUrl(downloadUrl));
  };

  const handleAddOrderToCalendar = async () => {
    if (!order) return;
    try {
      await addDriverOrderToCalendar(order, {
        createCalendarDownloadLink: DriverAPI.createOrderCalendarDownloadLink,
        openCalendarUrl: openCalendarDownloadUrl,
        resolveDownloadUrl: resolveDriverDownloadUrl,
        language: getDriverLanguage(),
      });
    } catch (err) {
      alert(getCalendarErrorMessage(err, text));
    }
  };

  const handleDriverStatusUpdate = async (status, options = {}) => {
    const previousDriverStatus = order?.driver_order_status || 'assigned';
    setUpdatingStatus(status);
    setStatusSuccess('');
    setStatusUpdateMessage('');
    if (status !== 'issue_reported') {
      setOrder((current) => current ? {
        ...current,
        driver_order_status: status,
        driver_issue_unresolved: false,
      } : current);
    }
    const client_event_id = options.client_event_id || createDriverClientEventId(orderId, status);
    let payloadForQueue = null;
    try {
      const location = await captureDriverStatusLocation();
      const payload = {
        location,
        event_time: new Date().toISOString(),
        client_event_id,
        ...(options.payload || {}),
      };
      payloadForQueue = payload;
      const result = await DriverAPI.updateOrderStatus(orderId, status, payload);
      const nextDriverStatus = result?.driver_order_status || status;
      setOrder((current) => current ? {
        ...current,
        driver_order_status: nextDriverStatus,
        driver_issue_unresolved: status === 'issue_reported' ? true : current.driver_issue_unresolved,
      } : current);
      setStatusSuccess(status === 'issue_reported' ? text.issueReport : (driverStatusLabel(nextDriverStatus) || text.statusUpdated));
      setStatusSyncMessage('');
      await playDriverSuccessFeedback();
      if (status === 'issue_reported') {
        setShowIssueReportPanel(false);
        setSelectedIssueTypes([]);
        setIssueNote('');
      }
      fetchOrderDetail();
    } catch (err) {
      if (!err.response) {
        queueDriverStatusUpdate({
          driver_id: offlineOrderPackDriverId,
          order_id: orderId,
          status,
          client_event_id,
          payload: payloadForQueue || {
            client_event_id,
            event_time: new Date().toISOString(),
            ...(options.payload || {}),
          },
        });
        const count = getQueuedDriverStatusUpdates(offlineOrderPackDriverId).length;
        setQueuedStatusCount(count);
        setStatusSyncMessage(text.statusQueued);
      } else {
        if (status !== 'issue_reported') {
          setOrder((current) => current ? {
            ...current,
            driver_order_status: previousDriverStatus,
          } : current);
        }
        alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
      }
    } finally {
      setUpdatingStatus('');
      setStatusUpdateMessage('');
    }
  };

  const toggleIssueType = (issueType) => {
    setSelectedIssueTypes((current) => (
      current.includes(issueType)
        ? current.filter((item) => item !== issueType)
        : [...current, issueType]
    ));
  };

  const handleSubmitIssueReport = () => {
    if (selectedIssueTypes.length === 0 && !issueNote.trim()) {
      alert(text.chooseIssue);
      return;
    }
    handleDriverStatusUpdate('issue_reported', {
      silentLocationMessage: true,
      payload: {
        issue_report: {
          issue_types: selectedIssueTypes,
          issue_note: issueNote.trim(),
        },
      },
    });
  };

  const handleCancelIssueReport = async () => {
    if (!window.confirm(text.cancelIssueReport)) return;
    setUpdatingStatus('cancel_issue_report');
    setStatusSuccess('');
    try {
      const result = await DriverAPI.cancelIssueReport(orderId);
      const nextDriverStatus = result?.driver_order_status || order?.driver_status_before_issue_report || 'assigned';
      setOrder((current) => current ? {
        ...current,
        driver_order_status: nextDriverStatus,
        driver_issue_unresolved: false,
      } : current);
      setStatusSuccess(text.issueReportCancelled);
      await playDriverSuccessFeedback();
      fetchOrderDetail();
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.submitFailed, driverLanguage));
    } finally {
      setUpdatingStatus('');
    }
  };

  const handleReturnOrderToPool = async () => {
    if (!window.confirm(text.returnOrderConfirm)) return;
    setUpdatingStatus('return_to_pool');
    setStatusSuccess('');
    try {
      await DriverAPI.returnOrderToPool(orderId, {
        reason: returnReason.trim(),
      });
      setStatusSuccess(text.returnOrderSuccess);
      await playDriverSuccessFeedback();
      window.setTimeout(() => navigate('/driver/orders', { replace: true }), 420);
    } catch (err) {
      alert(getLocalizedDriverApiError(err, text.returnOrderFailed, driverLanguage));
    } finally {
      setUpdatingStatus('');
    }
  };

  const handleOpenGoogleMaps = async () => {
    await openMapsForOrder(order, mapTarget, mapProvider);
    setShowMapConfirm(false);
  };

  const formatDate = (dateStr) => formatDriverDate(dateStr, { includeYear: true });

  const getAreaName = (code) => {
    return featureText.areas[code] || code || '-';
  };

  const currentDriverStatus = order?.driver_order_status || 'assigned';
  const hasActiveIssueReport = Boolean(order?.driver_issue_unresolved || currentDriverStatus === 'issue_reported');
  const normalizedCurrentStatus = currentDriverStatus === 'issue_reported'
    ? (order?.driver_status_before_issue_report || 'assigned')
    : currentDriverStatus;
  const getNextStatusAction = (status) => {
    if (status === 'completed') return null;
    if (DRIVER_PRE_PICKUP_STATUSES.has(status)) return DRIVER_STATUS_ACTIONS[0];
    if (status === 'arrived_pickup') return DRIVER_STATUS_ACTIONS[1];
    if (status === 'passenger_onboard' || status === 'in_service') return DRIVER_STATUS_ACTIONS[2];
    return DRIVER_STATUS_ACTIONS[0];
  };
  const nextStatusAction = getNextStatusAction(normalizedCurrentStatus);
  const isFleetOrderReadOnly = Boolean(
    order?.fleet_owner_order && order?.assigned_driver_id !== driver?.id
  );
  const canUseDriverOrderActions = !isFleetOrderReadOnly;
  const canUseFleetOrderTools = canUseDriverOrderActions || Boolean(order?.fleet_owner_order);
  const canReturnOrder = Boolean(
    canUseDriverOrderActions && order?.assigned_driver_id && normalizedCurrentStatus !== 'completed'
  );
  const changeHighlights = getOrderChangeHighlights(order, text);
  const renderChangeHighlights = () => {
    if (!changeHighlights.length) return null;
    return (
      <div className="driver-order-change-highlights" aria-label={text.orderChangeUpdated}>
        <span className="driver-change-highlight">{text.orderChangeUpdated}</span>
        {changeHighlights.map((label) => (
          <span key={label} className="driver-change-highlight">{label}</span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-muted-foreground">{text.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface p-4">
        <button
          onClick={() => navigate('/driver/orders')}
          className="mb-4 flex items-center gap-2 text-muted-foreground"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {text.back}
        </button>
        <div className="premium-chip-destructive rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface pb-6">
      {/* Header */}
      <DriverBrandHeader pageTitle={text.orderDetail} onBack={() => navigate('/driver/orders')} />

      <div className="p-4 space-y-4">
        {usingCachedData && (
          <div className="rounded-xl border border-[#d8b769]/30 bg-[#d8b769]/10 px-4 py-3 text-sm font-semibold text-[#f6d982]" aria-label={text.cachedData}>
            {text.cachedData}
          </div>
        )}
        {statusSuccess && (
          <div className="driver-status-success" data-testid="driver-status-success">
            {statusSuccess}
          </div>
        )}
        {(statusSyncMessage || queuedStatusCount > 0) && (
          <div className="driver-status-sync-banner" data-testid="driver-status-sync-banner">
            <span>{statusSyncMessage || statusPendingMessage(queuedStatusCount)}</span>
          </div>
        )}
        {renderChangeHighlights()}
        <OrderSeatBadges order={order} language={driverLanguage} />
        {/* Status Card */}
        <div className="px-0 py-1" data-testid="driver-order-status-summary">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {text.orderStatus}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl border border-[#d8b769]/20 bg-black/25 px-2 py-3">
              <div className="text-[11px] text-muted-foreground">{text.booking}</div>
              <div className="mt-1 text-sm font-semibold text-[#f6d982]">{statusLabel(order.status)}</div>
            </div>
            <div className="rounded-xl border border-[#d8b769]/20 bg-black/25 px-2 py-3">
              <div className="text-[11px] text-muted-foreground">{text.service}</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{serviceTypeLabel(order.service_type)}</div>
            </div>
            <div className="rounded-xl border border-[#d8b769]/20 bg-black/25 px-2 py-3">
              <div className="text-[11px] text-muted-foreground">{text.driver}</div>
              <div className="mt-1 text-sm font-semibold text-[#4fd1a5]">{driverStatusLabel(currentDriverStatus)}</div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.statusActions}</h3>
          {isFleetOrderReadOnly ? (
            <p className="rounded-xl border border-[#d8b769]/25 bg-[#d8b769]/10 px-3 py-3 text-sm font-semibold text-[#f6d982]" data-testid="fleet-order-detail-read-only">
              {text.fleetOrderReadOnly}
            </p>
          ) : (
          <div className="space-y-2">
            {nextStatusAction && normalizedCurrentStatus !== 'completed' && (
              <DriverStatusSwipeControl
                action={{ ...nextStatusAction, label: actionLabel(nextStatusAction.status) }}
                disabled={Boolean(updatingStatus)}
                updating={updatingStatus === nextStatusAction.status}
                onComplete={handleDriverStatusUpdate}
                text={text}
              />
            )}
            {hasActiveIssueReport && normalizedCurrentStatus !== 'completed' && (
              <button
                onClick={handleCancelIssueReport}
                disabled={Boolean(updatingStatus)}
                className="driver-status-issue-button"
                data-testid="driver-status-cancel-issue"
              >
                {updatingStatus === 'cancel_issue_report' ? text.submitting : text.cancelIssueReport}
              </button>
            )}
            {!hasActiveIssueReport && normalizedCurrentStatus !== 'completed' && (
              <button
                onClick={() => setShowIssueReportPanel(true)}
                disabled={Boolean(updatingStatus)}
                className="driver-status-issue-button"
                data-testid="driver-status-issue_reported"
              >
                {text.issueReport}
              </button>
            )}
            <DriverTripInstruction key={orderId} orderId={orderId} />
            {showIssueReportPanel && !hasActiveIssueReport && normalizedCurrentStatus !== 'completed' && (
              <div className="driver-issue-report-panel" data-testid="driver-issue-report-panel">
                <div className="driver-issue-report-title">{text.chooseIssue}</div>
                <div className="driver-issue-options">
                  {DRIVER_ISSUE_OPTIONS.map((option) => (
                    <label key={option.value} className="driver-issue-option">
                      <input
                        type="checkbox"
                        checked={selectedIssueTypes.includes(option.value)}
                        onChange={() => toggleIssueType(option.value)}
                      />
                      <span>{issueLabel(option.value)}</span>
                    </label>
                  ))}
                </div>
                <textarea
                  value={issueNote}
                  onChange={(event) => setIssueNote(event.target.value)}
                  className="driver-issue-report-note"
                  rows={4}
                  placeholder={text.issuePlaceholder}
                  data-testid="driver-issue-report-note"
                />
                <div className="driver-issue-report-actions">
                  <button
                    type="button"
                    onClick={() => setShowIssueReportPanel(false)}
                    disabled={Boolean(updatingStatus)}
                    className="driver-issue-report-secondary"
                  >
                    {text.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitIssueReport}
                    disabled={Boolean(updatingStatus)}
                    className="driver-issue-report-submit"
                  >
                    {updatingStatus === 'issue_reported' ? text.submitting : text.submitIssue}
                  </button>
                </div>
              </div>
            )}
            {canReturnOrder && (
              <button
                onClick={() => setShowReturnPanel((current) => !current)}
                disabled={Boolean(updatingStatus)}
                className="driver-return-order-toggle"
                data-testid="driver-return-order-toggle"
              >
                {text.returnOrder}
              </button>
            )}
            {showReturnPanel && canReturnOrder && (
              <div className="driver-return-order-panel" data-testid="driver-return-order-panel">
                <div className="driver-issue-report-title">{text.returnOrderTitle}</div>
                <textarea
                  value={returnReason}
                  onChange={(event) => setReturnReason(event.target.value)}
                  className="driver-issue-report-note"
                  rows={3}
                  placeholder={text.returnOrderPlaceholder}
                  data-testid="driver-return-order-note"
                />
                <DriverStatusSwipeControl
                  action={{ status: 'return_to_pool', label: text.returnOrderSwipe }}
                  disabled={Boolean(updatingStatus)}
                  updating={updatingStatus === 'return_to_pool'}
                  onComplete={handleReturnOrderToPool}
                  text={text}
                />
              </div>
            )}
          </div>
          )}
          {isFleetOrderReadOnly && <DriverTripInstruction key={orderId} orderId={orderId} />}
        </div>

        {canUseDriverOrderActions && (
        <div className="driver-emergency-card" data-testid="driver-emergency-contact-card">
          <div>
            <div className="driver-emergency-title">{text.emergencyContact}</div>
            <div className="driver-emergency-subtitle">{text.emergencyContactHint}</div>
          </div>
          <div className="driver-emergency-actions">
            <button
              type="button"
              onClick={() => callPhoneNumber(DRIVER_EMERGENCY_DISPATCH_PHONE)}
              className="driver-emergency-primary"
              data-testid="driver-emergency-call-dispatch"
            >
              {text.contactDispatch}
            </button>
            <button
              type="button"
              onClick={() => setShowIssueReportPanel(true)}
              className="driver-emergency-secondary"
              data-testid="driver-emergency-report-trouble"
            >
              {text.reportAccidentTrouble}
            </button>
          </div>
        </div>
        )}

        {/* Date & Time */}
        <div className="bg-card rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.dateTime}</h3>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-bold text-foreground">
              {order.pickup_time || '--:--'}
            </div>
            <div className="text-foreground">
              {formatDate(order.departure_date)}
            </div>
          </div>
          {order.service_type === 'charter' && order.selected_hours && (
            <div className="mt-2 text-sm text-muted-foreground">
              {text.charterDuration}: {order.selected_hours} {text.hours}
            </div>
          )}
        </div>

        {canViewRevenueStats && order.driver_dispatch_price_visible && (
          <div className="bg-card rounded-xl p-4 shadow-sm" data-testid="driver-order-detail-dispatch-price">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{text.dispatchPrice}</h3>
            <div className="driver-dispatch-price-value">
              {formatYen(order.driver_dispatch_price_amount)}
            </div>
          </div>
        )}

        {canUseDriverOrderActions && order?.trip_chat_available && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.tripCommunicationCenter}</h3>
            <button
              onClick={() => navigate(`/driver/orders/${orderId}/chat`)}
              className={`w-full rounded-xl px-4 py-3 font-semibold transition ${
                order?.trip_chat_unread
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
              data-testid="driver-trip-chat-button"
            >
              {order?.trip_chat_unread ? text.unreadEnterChat : text.enterChat}
            </button>
          </div>
        )}

        {order.dispatch_note && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.dispatchContact}</h3>
            <div className="rounded-xl bg-muted/50 p-3">
              <div className="text-xs text-muted-foreground mb-1">{text.dispatchNote}</div>
              <p className="whitespace-pre-wrap text-sm text-foreground">{order.dispatch_note}</p>
            </div>
          </div>
        )}

        {/* Location */}
        <div className="bg-card rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.tripInfo}</h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-success"></div>
                <div className="w-0.5 h-8 bg-muted"></div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{text.pickup}</div>
                <div className="font-medium">{getAreaName(order.pickup_area)}</div>
                {order.pickup_location_detail && (
                  <div className="text-sm text-muted-foreground mt-1">{order.pickup_location_detail}</div>
                )}
                {order.pickup_terminal && (
                  <div className="text-sm text-muted-foreground">{text.terminal}: {order.pickup_terminal}</div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-destructive"></div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{text.dropoff}</div>
                <div className="font-medium">
                  {getAreaName(order.dropoff_area) || order.destination || order.end_location || '-'}
                </div>
                {order.dropoff_location_detail && (
                  <div className="text-sm text-muted-foreground mt-1">{order.dropoff_location_detail}</div>
                )}
                {order.dropoff_terminal && (
                  <div className="text-sm text-muted-foreground">{text.terminal}: {order.dropoff_terminal}</div>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowMapConfirm(true)}
            className="mt-4 w-full rounded-xl border border-[#d8b769]/35 bg-[#d8b769]/10 px-4 py-3 font-semibold text-[#f6d982]"
            data-testid="driver-map-confirm-button"
          >
            {text.openMap}
          </button>
        </div>

        {/* Flight Info */}
        {order.flight_number && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{text.flightNumber}</h3>
            <div className="text-lg font-semibold">{order.flight_number}</div>
          </div>
        )}

        {/* Passenger Info */}
        <div className="bg-card rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.passengerInfo}</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{text.passengerName}</span>
              <span className="font-medium">{order.customer_name || '-'}</span>
            </div>
            {order.customer_code && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{text.customerCode}</span>
                <span className="font-medium">{order.customer_code}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{text.phone}</span>
              <a 
                href={`tel:${order.phone}`} 
                className="font-medium text-primary underline"
              >
                {order.phone || '-'}
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{text.passengerCount}</span>
              <span className="font-medium">{order.passenger_count || 1} {text.peopleUnit}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{text.luggageCount}</span>
              <span className="font-medium">{order.luggage_count || 0} {text.luggageUnit}</span>
            </div>

          </div>
          {canUseFleetOrderTools && (
          <div className={`mt-4 grid gap-2 ${canUseDriverOrderActions ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <button
              onClick={handleDownloadSignboardPdf}
              disabled={generatingSignboard}
              className="w-full bg-primary text-primary-foreground rounded-xl py-3 font-semibold hover:brightness-110 disabled:opacity-50 transition"
              data-testid="download-signboard-pdf-button"
            >
              {generatingSignboard ? text.downloading : text.downloadPdf}
            </button>
            {canUseDriverOrderActions && <button
              type="button"
              onClick={handleAddOrderToCalendar}
              className={`w-full rounded-xl py-3 font-semibold transition ${
                getDriverCalendarAdded(orderId)
                  ? 'bg-success/15 text-success border border-success/30'
                  : 'bg-surface text-foreground border border-border-subtle hover:bg-muted'
              }`}
              data-testid="add-calendar-button"
            >
              {getDriverCalendarAdded(orderId) ? text.addedToCalendar : text.addToCalendar}
            </button>}
          </div>
          )}
        </div>

        {/* Vehicle Info */}
        <div className="bg-card rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.vehicleInfo}</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{text.vehicleType}</span>
              <span className="font-medium">{formatDriverVehicleType(order.vehicle_type)}</span>
            </div>
            {order.assigned_driver?.plate_number && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{text.plateNumber}</span>
                <span className="font-medium">{order.assigned_driver.plate_number}</span>
              </div>
            )}
            {order.assigned_driver?.vehicle_brand && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{text.brand}</span>
                <span className="font-medium">{order.assigned_driver.vehicle_brand}</span>
              </div>
            )}
          </div>
        </div>

        {/* Extra Services */}
        {visibleExtraServices.length > 0 && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.extraServices}</h3>
            <div className="space-y-2">
              {visibleExtraServices.map((service, index) => (
                <div key={index} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{extraServiceLabel(service)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trip Details / Notes */}
        {(order.trip_details || order.other_service_desc) && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{text.notes}</h3>
            <p className="text-foreground whitespace-pre-wrap">
              {order.trip_details || order.other_service_desc}
            </p>
          </div>
        )}

        {/* 汇报包车时间按钮 - 仅包车订单显示 */}
        {canUseFleetOrderTools && order.service_type === 'charter' && (
          <div className="bg-card rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{text.reportCharterTime}</h3>
            
            {order.reported_end_time ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{text.reportedEndTime}</span>
                  <span className="font-bold text-foreground">{order.reported_end_time}</span>
                </div>
                <button
                  onClick={() => setShowReportModal(true)}
                  className="w-full py-2 bg-surface text-foreground rounded-xl hover:bg-muted transition text-sm"
                  data-testid="modify-end-time-button"
                >
                  {text.modifyEndTime}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowReportModal(true)}
                className="w-full bg-warning text-warning-foreground rounded-xl font-semibold hover:brightness-110 transition"
                data-testid="report-end-time-button"
              >
                {text.reportEndTime}
              </button>
            )}
          </div>
        )}

        {/* Order number */}
        <div className="text-center text-xs text-muted-foreground mt-4">
          {text.orderNo}: {getOrderNo(order)}
        </div>
      </div>

      {/* 汇报包车时间弹窗 */}
      {showReportModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="premium-card w-full max-w-sm">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{text.reportEndTime}</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-muted-foreground hover:text-foreground text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* 订单信息 */}
              <div className="bg-surface rounded-xl p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-muted-foreground">{text.startTime}</span>
                  <span className="font-medium">{order.pickup_time || '--:--'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{text.bookedDuration}</span>
                  <span className="font-medium">{order.selected_hours || '-'} {text.hours}</span>
                </div>
              </div>

              {/* 时间选择 */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {text.actualEndTime}
                </label>
                <select
                  ref={selectRef}
                  onChange={(e) => setReportEndTime(e.target.value)}
                  className="premium-input text-base"
                  data-testid="end-time-select"
                />
              </div>

              {/* 提交按钮 */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-3 border border-border-subtle rounded-xl font-medium hover:bg-surface transition"
                >
                  {text.cancel}
                </button>
                <button
                  onClick={handleReportEndTime}
                  disabled={reporting || !reportEndTime}
                  className="flex-1 py-3 bg-warning text-warning-foreground rounded-xl font-semibold hover:brightness-110 disabled:opacity-50 transition"
                  data-testid="submit-end-time-button"
                >
                  {reporting ? text.submitting : text.submitConfirm}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showMapConfirm && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 driver-fixed-overlay">
          <div className="driver-map-confirm-panel w-full" data-testid="driver-map-confirm-panel" aria-label={text.mapConfirm}>
            <div className="mb-3 text-center text-lg font-black text-[#f6d982]">{text.mapConfirm}</div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-muted-foreground">{text.pickup}</div>
                <div className="font-semibold text-foreground">
                  {[getAreaName(order.pickup_area), order.pickup_location_detail, order.pickup_terminal, order.flight_number].filter(Boolean).join(' / ') || '-'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">{text.dropoff}</div>
                <div className="font-semibold text-foreground">
                  {[getAreaName(order.dropoff_area), order.dropoff_location_detail || order.destination || order.end_location, order.dropoff_terminal].filter(Boolean).join(' / ') || '-'}
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                { key: 'pickup', label: text.pickup },
                { key: 'route', label: text.route },
                { key: 'dropoff', label: text.dropoff },
              ].map((target) => (
                <button
                  key={target.key}
                  type="button"
                  onClick={() => setMapTarget(target.key)}
                  className={`rounded-xl border px-3 py-2 text-sm font-bold ${mapTarget === target.key ? 'border-[#f6d982] bg-[#f6d982] text-black' : 'border-[#d8b769]/30 text-[#f6d982]'}`}
                >
                  {target.label}
                </button>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                { key: 'apple', label: text.appleMaps },
                { key: 'google', label: text.googleMaps },
              ].map((provider) => (
                <button
                  key={provider.key}
                  type="button"
                  onClick={() => setMapProvider(provider.key)}
                  className={`rounded-xl border px-3 py-2 text-sm font-bold ${mapProvider === provider.key ? 'border-[#f6d982] bg-[#f6d982] text-black' : 'border-[#d8b769]/30 text-[#f6d982]'}`}
                >
                  {provider.label}
                </button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowMapConfirm(false)}
                className="rounded-xl border border-[#d8b769]/25 px-4 py-3 font-semibold text-muted-foreground"
              >
                {text.cancel}
              </button>
              <button
                type="button"
                onClick={handleOpenGoogleMaps}
                className="rounded-xl bg-[#f6d982] px-4 py-3 font-black text-black"
              >
                {text.startNavigation}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
